import React, { useState, useEffect } from 'react';
import CustomerHeader from '../components/CustomerHeader';
import './Contact.css';
import { supabase } from '../config/supabase';
import { getRestaurantInfo } from '../utils/restaurantInfo';
import { getOperatingHours } from '../utils/operatingHours';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [restaurantInfo, setRestaurantInfo] = useState({
    address: 'DHA Phase 6, Lahore, Punjab, Pakistan',
    phone: '+92 300 1234567',
    email: 'info@fooddelivery.com'
  });
  const [operatingHours, setOperatingHours] = useState([]);

  // Fetch restaurant info from settings
  useEffect(() => {
    const loadRestaurantInfo = async () => {
      try {
        const { data, error } = await getRestaurantInfo();
        if (!error && data) {
          setRestaurantInfo({
            address: data.address || 'DHA Phase 6, Lahore, Punjab, Pakistan',
            phone: data.phone || '+92 300 1234567',
            email: data.email || 'info@fooddelivery.com'
          });
        }
      } catch (error) {
        console.error('Error loading restaurant info:', error);
      }
    };
    loadRestaurantInfo();
  }, []);

  // Fetch operating hours from settings
  useEffect(() => {
    const loadOperatingHours = async () => {
      try {
        const { data, error } = await getOperatingHours();
        if (!error && data && data.operating_hours) {
          const hours = data.operating_hours;
          const days = [
            { key: 'monday', label: 'Monday' },
            { key: 'tuesday', label: 'Tuesday' },
            { key: 'wednesday', label: 'Wednesday' },
            { key: 'thursday', label: 'Thursday' },
            { key: 'friday', label: 'Friday' },
            { key: 'saturday', label: 'Saturday' },
            { key: 'sunday', label: 'Sunday' }
          ];

          // Format hours for display
          const formattedHours = [];
          let currentGroup = null;

          days.forEach(day => {
            const dayData = hours[day.key];
            if (dayData && dayData.enabled) {
              const timeStr = `${formatTime(dayData.open)} - ${formatTime(dayData.close)}`;
              
              if (currentGroup && currentGroup.time === timeStr) {
                // Add to existing group
                currentGroup.days.push(day.label);
              } else {
                // Start new group
                if (currentGroup) {
                  formattedHours.push(currentGroup);
                }
                currentGroup = {
                  days: [day.label],
                  time: timeStr
                };
              }
            }
          });

          // Add last group
          if (currentGroup) {
            formattedHours.push(currentGroup);
          }

          setOperatingHours(formattedHours);
        } else {
          // Default hours if not found
          setOperatingHours([
            { days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], time: '10:00 AM - 10:00 PM' },
            { days: ['Saturday', 'Sunday'], time: '11:00 AM - 11:00 PM' }
          ]);
        }
      } catch (error) {
        console.error('Error loading operating hours:', error);
        // Default hours on error
        setOperatingHours([
          { days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], time: '10:00 AM - 10:00 PM' },
          { days: ['Saturday', 'Sunday'], time: '11:00 AM - 11:00 PM' }
        ]);
      }
    };
    loadOperatingHours();
  }, []);

  const formatTime = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const formatDaysRange = (days) => {
    if (days.length === 1) return days[0];
    if (days.length === 2) return `${days[0]} - ${days[1]}`;
    return `${days[0]} - ${days[days.length - 1]}`;
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    // Validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      // Insert contact message into Supabase
      const { data, error: insertError } = await supabase
        .from('contact_messages')
        .insert([
          {
            name: formData.name.trim(),
            email: formData.email.toLowerCase().trim(),
            phone: formData.phone.trim() || null,
            subject: formData.subject.trim() || null,
            message: formData.message.trim(),
            status: 'new'
          }
        ])
        .select()
        .single();

      if (insertError) throw insertError;

      if (data) {
        setSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });

        setTimeout(() => {
          setSuccess(false);
        }, 5000);
      }
    } catch (err) {
      console.error('Error submitting contact form:', err);
      setError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">
      <CustomerHeader
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />

      {/* Page Header */}
      <div className="contact-page-header">
        <div className="contact-page-header-container">
          <h1 className="contact-page-title">Contact Us</h1>
          <p className="contact-page-subtitle">
            We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="contact-page-main">
        <div className="contact-page-container">
          <div className="contact-page-content">
            {/* Left Column - Contact Form */}
            <div className="contact-page-form-section">
              <h2 className="contact-page-form-title">Send us a Message</h2>
              
              {error && (
                <div className="contact-page-message contact-page-error">
                  <span className="contact-page-message-icon">⚠️</span>
                  <span className="contact-page-message-text">{error}</span>
                </div>
              )}

              {success && (
                <div className="contact-page-message contact-page-success">
                  <span className="contact-page-message-icon">✓</span>
                  <span className="contact-page-message-text">
                    Thank you! Your message has been sent successfully. We'll get back to you soon.
                  </span>
                </div>
              )}

              <form className="contact-page-form" onSubmit={handleSubmit}>
                <div className="contact-page-form-row">
                  <div className="contact-page-form-group">
                    <label className="contact-page-label">
                      Full Name <span className="contact-page-required">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      className="contact-page-input"
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="contact-page-form-group">
                    <label className="contact-page-label">
                      Email Address <span className="contact-page-required">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      className="contact-page-input"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="contact-page-form-row">
                  <div className="contact-page-form-group">
                    <label className="contact-page-label">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      className="contact-page-input"
                      placeholder="Enter your phone number"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="contact-page-form-group">
                    <label className="contact-page-label">Subject</label>
                    <input
                      type="text"
                      name="subject"
                      className="contact-page-input"
                      placeholder="What is this regarding?"
                      value={formData.subject}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="contact-page-form-group">
                  <label className="contact-page-label">
                    Message <span className="contact-page-required">*</span>
                  </label>
                  <textarea
                    name="message"
                    className="contact-page-textarea"
                    placeholder="Tell us how we can help you..."
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="contact-page-submit-btn"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>

            {/* Right Column - Contact Information */}
            <div className="contact-page-info-section">
              <h2 className="contact-page-info-title">Get in Touch</h2>
              
              <div className="contact-page-info-card">
                <div className="contact-page-info-item">
                  <div className="contact-page-info-icon">📍</div>
                  <div className="contact-page-info-content">
                    <h3 className="contact-page-info-label">Address</h3>
                    <p className="contact-page-info-text" style={{ whiteSpace: 'pre-line' }}>
                      {restaurantInfo.address}
                    </p>
                  </div>
                </div>

                <div className="contact-page-info-item">
                  <div className="contact-page-info-icon">📞</div>
                  <div className="contact-page-info-content">
                    <h3 className="contact-page-info-label">Phone</h3>
                    <p className="contact-page-info-text">
                      <a href={`tel:${restaurantInfo.phone.replace(/\s/g, '')}`} className="contact-page-link">
                        {restaurantInfo.phone}
                      </a>
                    </p>
                  </div>
                </div>

                <div className="contact-page-info-item">
                  <div className="contact-page-info-icon">✉️</div>
                  <div className="contact-page-info-content">
                    <h3 className="contact-page-info-label">Email</h3>
                    <p className="contact-page-info-text">
                      <a href={`mailto:${restaurantInfo.email}`} className="contact-page-link">
                        {restaurantInfo.email}
                      </a>
                    </p>
                  </div>
                </div>

                <div className="contact-page-info-item">
                  <div className="contact-page-info-icon">🕒</div>
                  <div className="contact-page-info-content">
                    <h3 className="contact-page-info-label">Business Hours</h3>
                    <div className="contact-page-hours">
                      {operatingHours.length > 0 ? (
                        operatingHours.map((group, index) => (
                          <div key={index} className="contact-page-hours-row">
                            <span className="contact-page-hours-day">{formatDaysRange(group.days)}</span>
                            <span className="contact-page-hours-time">{group.time}</span>
                          </div>
                        ))
                      ) : (
                        <>
                          <div className="contact-page-hours-row">
                            <span className="contact-page-hours-day">Monday - Friday</span>
                            <span className="contact-page-hours-time">10:00 AM - 10:00 PM</span>
                          </div>
                          <div className="contact-page-hours-row">
                            <span className="contact-page-hours-day">Saturday - Sunday</span>
                            <span className="contact-page-hours-time">11:00 AM - 11:00 PM</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;

